import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { useTheme } from "../../../../../theme/OAS365Theme";

function spacePascalCase(str) {
  if (!str || str === "–") return str;
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    const raw = String(dateStr).trim();
    const d = new Date(raw);
    if (isNaN(d.getTime())) return dateStr;

    // Keep source timestamp values (IFS-style) instead of converting to browser local time.
    const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
    const year = hasTimeZone ? d.getUTCFullYear() : d.getFullYear();
    const month = (hasTimeZone ? d.getUTCMonth() : d.getMonth()) + 1;
    const day = hasTimeZone ? d.getUTCDate() : d.getDate();
    const hour24 = hasTimeZone ? d.getUTCHours() : d.getHours();
    const minute = hasTimeZone ? d.getUTCMinutes() : d.getMinutes();
    const second = hasTimeZone ? d.getUTCSeconds() : d.getSeconds();

    const ampm = hour24 >= 12 ? "PM" : "AM";
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const mm = String(minute).padStart(2, "0");
    const ss = String(second).padStart(2, "0");
    const isoDate = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`; // ISO 8601: YYYY-MM-DD

    return `${isoDate}, ${hour12}:${mm}:${ss} ${ampm}`;
  } catch {
    return dateStr;
  }
}

function HistoryRow({ row, isLast, theme }) {
  const lineNo = row.LineNo ?? row.lineNo ?? "–";
  const infoCategory = spacePascalCase(
    row.InfoCategory ?? row.infoCategory ?? "–",
  );
  const status = row.Status ?? row.status ?? "";
  const note = row.Note ?? row.note ?? "–";
  const createdBy = row.CreatedBy ?? row.createdBy ?? "–";
  const name = row.CreatedByName ?? row.createdByName ?? "–";
  const formatted = formatDate(row.DateCreated ?? row.dateCreated);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns:
          "110px 160px 200px minmax(300px, 2fr) 200px 170px",
        gap: 0.75,
        alignItems: "flex-start",
        px: 0.5,
        width: "100%",
        pt: 0.5,
        pb: isLast ? 0.5 : 1,
        borderBottom: isLast
          ? "none"
          : `1px solid ${theme.colors?.dialogBorderColor || theme.colors?.borderColor || "#F0F0F0"}`,
      }}
    >
      {/* Line */}
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 500,
          color: theme.colors?.fontColor,
          fontFamily: theme.fonts?.regular,
          pt: "4px",
          lineHeight: 1.25,
          textAlign: "center",
        }}
      >
        {lineNo}
      </Typography>

        {/* Info Category */}
        <Tooltip title={infoCategory} placement="top" arrow>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 400,
              color: theme.colors?.fontColor,
              fontFamily: theme.fonts?.regular,
              pt: "4px",
              lineHeight: 1.25,
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {infoCategory}
          </Typography>
        </Tooltip>

        {/* Status */}
        <Box sx={{ pt: "2px", minWidth: 0 }}>
          {status ? (
            <Tooltip title={status} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: theme.colors?.fontColor,
                  fontFamily: theme.fonts?.regular,
                  fontWeight: 400,
                  pt: "2px",
                  lineHeight: 1.25,
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {status}
              </Typography>
            </Tooltip>
          ) : (
            <Typography
                sx={{
                  fontSize: "13px",
                  color: theme.colors?.fontColor,
                  pt: "4px",
                  lineHeight: 1.25,
                }}
              >
                –
              </Typography>
          )}
        </Box>

        {/* Note */}
        <Tooltip title={note} placement="top" arrow>
          <Typography
            sx={{
              fontSize: "13px",
              color: theme.colors?.fontColor,
              fontFamily: theme.fonts?.regular,
              pt: "4px",
              pr: 1,
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {note}
          </Typography>
        </Tooltip>

        {/* User */}
        <Box sx={{ pt: "4px", pl: 0.5, minWidth: 0 }}>
          <Tooltip title={name !== "–" ? name : createdBy} placement="top" arrow>
            <Typography
              sx={{
                fontSize: "13px",
                color: theme.colors?.fontColor,
                fontFamily: theme.fonts?.regular,
                lineHeight: 1.25,
                minWidth: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {name !== "–" ? name : createdBy}
            </Typography>
          </Tooltip>
          {name !== "–" && createdBy !== "–" && (
            <Typography
              sx={{
                fontSize: "10px",
                color: theme.colors?.fontColor,
                opacity: 0.55,
                fontFamily: theme.fonts?.regular,
                lineHeight: 1.15,
              }}
            >
              {createdBy}
            </Typography>
          )}
        </Box>

        {/* Created */}
        <Tooltip title={formatted ?? "–"} placement="top" arrow>
          <Typography
            sx={{
              fontSize: "12px",
              color: theme.colors?.fontColor,
              fontFamily: theme.fonts?.regular,
              pt: "4px",
              lineHeight: 1.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {formatted ?? "–"}
          </Typography>
        </Tooltip>
    </Box>
  );
}

export function IfsHistoryDialog({
  isOpen,
  onClose,
  historyData = [],
  isLoading = false,
  error = null,
  document = null,
}) {
  const theme = useTheme();
  const docTitle = document?.name || "Document";

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(98vw, 1320px)",
          maxHeight: "82vh",
          borderRadius: "12px",
          backgroundColor:
            theme.colors?.dialogBackgroundColor || theme.colors?.pureColor,
          color: theme.colors?.fontColor,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
        },
      }}
    >
      <Box
        sx={{
          height: 6,
          width: "100%",
          background:
            "linear-gradient(90deg, rgb(36, 159, 93) 0%, rgb(226, 239, 223) 98.79%)",
          flexShrink: 0,
        }}
      />

      {/* Header */}
      <DialogTitle
        sx={{
          pl: 2,
          pb: 1.25,
          pt: 1.5,
          pr: 2,
          borderBottom: `1px solid ${theme.colors?.dialogBorderColor || theme.colors?.borderColor || "#E0E0E0"}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          {/* Green icon */}
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "10px",
              backgroundColor:
                theme.colors?.themeDarkColor ||
                theme.colors?.themeColor ||
                "#3F9A52",
              border: `1px solid ${theme.colors?.borderColor || "rgba(0,0,0,0.08)"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <HistoryOutlinedIcon
              sx={{
                fontSize: 26,
                color: theme.colors?.addovationWhite || "#fff",
              }}
            />
          </Box>
          {/* Title block */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                color: theme.colors?.fontColor,
                fontFamily: theme.fonts?.regular,
                lineHeight: 1.3,
              }}
            >
              IFS History
            </Typography>
            <Tooltip
              title={docTitle}
              placement="top"
              arrow
              slotProps={{
                popper: {
                  modifiers: [
                    { name: "flip", enabled: false },
                    { name: "preventOverflow", enabled: false },
                    { name: "offset", options: { offset: [0, 8] } },
                  ],
                },
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: "12px",
                  color: theme.colors?.fontColor,
                  opacity: 0.62,
                  fontFamily: theme.fonts?.regular,
                  mt: 0.25,
                  lineHeight: 1.35,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "inline-block",
                  verticalAlign: "top",
                  maxWidth: "100%",
                }}
              >
                {docTitle}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      {/* Body */}
      <DialogContent
        sx={{
          px: 1,
          pt: 2,
          pb: 1,
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 220,
            }}
          >
            <CircularProgress sx={{ color: theme.colors?.themeColor || "#2E7D32" }} />
          </Box>
        )}
        {!isLoading && error && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 220,
            }}
          >
            <Typography sx={{ color: theme.colors?.errorColor || "#D92D20" }}>
              {error}
            </Typography>
          </Box>
        )}
        {!isLoading && !error && historyData.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 220,
            }}
          >
            <Typography sx={{ color: theme.colors?.fontColor, opacity: 0.6 }}>
              No history available for this document
            </Typography>
          </Box>
        )}
        {!isLoading && !error && historyData.length > 0 && (
          <Box sx={{ width: "100%", minWidth: 0 }}>
            {/* Column headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "110px 160px 200px minmax(300px, 2fr) 200px 170px",
                gap: 0.75,
                px: 0.5,
                py: 1,
                mb: 0.5,
                width: "100%",
                border: `1px solid ${theme.colors?.dialogBorderColor || theme.colors?.borderColor || "#E5E7EB"}`,
                borderRadius: "8px",
                backgroundColor: theme.colors?.tableHeaderBg || "#F9FAFB",
              }}
            >
              {[
                "Line",
                "Info Category",
                "Status",
                "Note",
                "Modified By",
                "Modified",
              ].map((col) => (
                <Typography
                  key={col}
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: theme.colors?.fontColor,
                    fontFamily: theme.fonts?.regular,
                    textAlign: col === "Line" ? "center" : "left",
                  }}
                >
                  {col}
                </Typography>
              ))}
            </Box>
            {/* Rows */}
            <Box
              sx={{
                pt: 0.5,
                maxHeight: "48vh",
                overflowY: "auto",
                overflowX: "hidden",
                scrollbarWidth: "thin",
                scrollbarColor: `${theme.colors?.scrollbarThumb || "#ccc"} ${theme.colors?.scrollbarBackground || "transparent"}`,
                "&::-webkit-scrollbar": {
                  width: 10,
                },
                "&::-webkit-scrollbar-track": {
                  background: theme.colors?.scrollbarBackground || "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: theme.colors?.scrollbarThumb || "#ccc",
                  borderRadius: "10px",
                  border: `2px solid ${theme.colors?.scrollbarBackground || "transparent"}`,
                },
              }}
            >
              {historyData.map((row, index) => (
                <HistoryRow
                  key={index}
                  row={row}
                  isLast={index === historyData.length - 1}
                  theme={theme}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <Divider />
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography
            sx={{
              fontSize: "13px",
              color: theme.colors?.fontColor,
              opacity: 0.75,
              fontFamily: theme.fonts?.regular,
            }}
          >
            {isLoading
              ? "Loading…"
              : `Showing ${historyData.length} history record(s)`}
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            borderColor: theme.colors?.dialogCloseBorder || "#57A77A",
            color: theme.colors?.dialogCloseText || "#57A77A",
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 500,
            fontFamily: theme.fonts?.regular,
            borderRadius: "999px",
            minWidth: 66,
            height: 30,
            px: 2,
            backgroundColor: theme.colors?.dialogCloseBg || "#FFFFFF",
            "&:hover": {
              borderColor: theme.colors?.dialogCloseHoverBorder || "#4A9A6C",
              backgroundColor: theme.colors?.dialogCloseHoverBg || "#F7FCF8",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
